import React, { useEffect, useRef } from 'react';
import * as Matter from 'matter-js';

const TrickyTowerBackground = ({ reorganize = false }) => {
    const sceneRef = useRef(null);
    const engineRef = useRef(null);
    const renderRef = useRef(null);
    const runnerRef = useRef(null);
    const reorganizeRef = useRef(false);
    const targetMapRef = useRef(new Map());
    const spawnActiveRef = useRef(true);

    // Sync reorganize prop to ref
    useEffect(() => {
        reorganizeRef.current = reorganize;
        if (reorganize) {
            spawnActiveRef.current = false;
        }
    }, [reorganize]);

    useEffect(() => {
        // 1. Initialisation du moteur physique
        const Engine = Matter.Engine,
            Render = Matter.Render,
            Runner = Matter.Runner,
            Bodies = Matter.Bodies,
            Composite = Matter.Composite,
            Body = Matter.Body,
            Events = Matter.Events;

        const engine = Engine.create();
        const world = engine.world;
        engineRef.current = engine;

        // Dimensions réelles du conteneur (pas window.innerHeight qui dépasse le slide)
        const W = sceneRef.current.offsetWidth;
        const H = sceneRef.current.offsetHeight;

        // Configuration de la scène (Canvas)
        const render = Render.create({
            element: sceneRef.current,
            engine: engine,
            options: {
                width: W,
                height: H,
                wireframes: false, // Mettre true pour voir les lignes de collision
                background: 'transparent', // Fond transparent pour s'intégrer au slide
            }
        });
        renderRef.current = render;

        // 2. Création du sol (Statique)
        // 2. Création du sol et des murs (Statique)
        const wallOptions = { isStatic: true, render: { fillStyle: 'rgba(90, 54, 248, 0.42)' } };
        const ground = Bodies.rectangle(
            W / 2,
            H,
            W,
            60,
            wallOptions
        );

        // Mur droit (pour empêcher de dépasser)
        const rightWall = Bodies.rectangle(
            W * 0.96, // Visible à droite
            H / 2,
            20,
            H,
            wallOptions
        );

        // Mur gauche (pour créer le couloir)
        const leftWall = Bodies.rectangle(
            W * 0.50, // Au milieu de l'écran
            H / 2,
            20,
            H,
            wallOptions
        );

        Composite.add(world, [ground, rightWall, leftWall]);

        // 3. Fonction pour créer des formes Tetris (Polyminos)
        const createBlock = () => {
            // Apparition uniquement sur la partie droite (entre 50% et 90% de la largeur)
            const minX = W * 0.55;
            const maxX = W * 0.85;
            const x = Math.random() * (maxX - minX) + minX;

            const y = -100; // Commence au-dessus de l'écran
            const size = 40; // Taille de base d'un "carré"

            const types = ['I', 'O', 'T', 'L', 'Z'];
            const type = types[Math.floor(Math.random() * types.length)];

            // Cyberpunk Palette
            const colors = [
                "#ff6b6b", // Zhongo Bleu principal
                "#1e40af", // Eurocorp Bleu foncé
                "#833a01", // Pétrolistan Marron
                "#2e3949ff", // Volgovie Gris foncé
                "#8b5cf6", // Autoristan Indigo
                "#f59e0b", // Sudica Violet
                "white", // Neo-Atlas blanc
                "#059669"  // Bricologia Emeraude
            ];
            const color = colors[Math.floor(Math.random() * colors.length)];

            // Style options
            const commonOptions = {
                restitution: 0.1,
                friction: 0.9,
                frictionStatic: 1.0,
                density: 0.002,
            };

            const getRandomColor = () => colors[Math.floor(Math.random() * colors.length)];
            let body;

            // Définition des formes
            switch (type) {
                case 'I': // Barre longue
                    body = Bodies.rectangle(x, y, size, size * 4, {
                        ...commonOptions, label: 'block-I',
                        render: { fillStyle: getRandomColor() }
                    });
                    break;
                case 'O': // Carré
                    body = Bodies.rectangle(x, y, size * 2, size * 2, {
                        ...commonOptions, label: 'block-O',
                        render: { fillStyle: getRandomColor() }
                    });
                    break;
                case 'T': // Forme en T
                    const colorT = getRandomColor()
                    body = Body.create({
                        parts: [
                            Bodies.rectangle(x, y, size * 3, size, { render: { fillStyle: colorT } }),
                            Bodies.rectangle(x, y + size, size, size, { render: { fillStyle: colorT } })
                        ],
                        ...commonOptions, label: 'block-T'
                    });
                    break;
                case 'L': // Forme en L
                    const colorL = getRandomColor()
                    body = Body.create({
                        parts: [
                            Bodies.rectangle(x, y, size, size * 3, { render: { fillStyle: colorL } }),
                            Bodies.rectangle(x + size, y + size, size, size, { render: { fillStyle: colorL } })
                        ],
                        ...commonOptions, label: 'block-L'
                    });
                    break;
                case 'Z': // Forme en Z
                    const colorZ = getRandomColor()
                    body = Body.create({
                        parts: [
                            Bodies.rectangle(x, y, size * 2, size, { render: { fillStyle: colorZ } }),
                            Bodies.rectangle(x + size, y + size, size * 2, size, { render: { fillStyle: colorZ } })
                        ],
                        ...commonOptions, label: 'block-Z'
                    });
                    break;
                default:
                    body = Bodies.rectangle(x, y, size, size, { ...commonOptions, label: 'block-O', render: { fillStyle: getRandomColor() } });
            }

            // Rotation aléatoire initiale
            const randomAngle = (Math.PI / 2) * Math.floor(Math.random() * 4);
            Body.setAngle(body, randomAngle);

            // Petite impulsion
            Body.setVelocity(body, { x: (Math.random() - 0.5) * 2, y: 0 });

            return body;
        };

        // 5. Boucle d'apparition dynamique
        let isRunning = true;
        let currentDelay = 2000; // Départ lent (2s)
        const minDelay = 400;   // Vitesse max (0.4s)

        const spawnLoop = () => {
            if (!isRunning) return;
            if (!spawnActiveRef.current) {
                setTimeout(spawnLoop, 500);
                return;
            }

            // Limiteur : ne pas ajouter de briques si on en a déjà trop (performance)
            // On compte seulement les briques dynamiques (qui ne sont pas statiques comme les murs)
            const dynamicBodies = Composite.allBodies(world).filter(b => !b.isStatic).length;

            if (dynamicBodies < 60) {
                const block = createBlock();
                Composite.add(world, block);
            }

            // Accélération progressive (ex: -50ms par brique jusqu'au min)
            currentDelay = Math.max(minDelay, currentDelay - 50);

            setTimeout(spawnLoop, currentDelay);
        };

        // Démarrage de la boucle après un court délai
        setTimeout(spawnLoop, 1000);

        // 6. Nettoyage des briques tombées + logique magnétique de réorganisation

        // Tower layout:
        // - I pieces (40x160) → laid horizontal (targetAngle=PI/2 → 160w x 40h), stacked as base rows
        // - O pieces (80x80) → stacked on top of the I rows
        // - T, L, Z → animated off-screen then removed
        const computeTowerTargets = (iBlocks, oBlocks) => {
            const wallLeft = W * 0.55 + 15;
            const wallRight = W * 0.85 - 15;
            const groundY = H - 40;
            const centerX = (wallLeft + wallRight) / 2;

            // I pieces horizontal: 160w x 40h
            const iW = 160, iH = 40;
            const targets = new Map();
            let cursorY = groundY;

            // Stack I pieces: one per row, centered
            for (let i = 0; i < iBlocks.length; i++) {
                cursorY -= iH / 2 + (i === 0 ? 0 : iH / 2);
                targets.set(iBlocks[i].id, {
                    target: { x: centerX, y: cursorY },
                    targetAngle: Math.PI / 2,
                    settled: false,
                    remove: false
                });
                if (i < iBlocks.length - 1) cursorY -= 2; // small gap
            }

            // O pieces on top: 80w x 80h, stack centered
            const oW = 80, oH = 80;
            const topOfI = cursorY - iH / 2;
            let oCursorY = topOfI;

            for (let i = 0; i < oBlocks.length; i++) {
                oCursorY -= oH / 2 + (i === 0 ? 0 : oH / 2);
                targets.set(oBlocks[i].id, {
                    target: { x: centerX, y: oCursorY },
                    targetAngle: 0,
                    settled: false,
                    remove: false
                });
                if (i < oBlocks.length - 1) oCursorY -= 2;
            }

            return targets;
        };

        let reorganizeInitialized = false;
        const bodiesToRemove = [];

        Events.on(engine, 'beforeUpdate', () => {
            const bodies = Composite.allBodies(world);

            if (reorganizeRef.current) {
                const allBlocks = bodies.filter(b => b.label && b.label.startsWith('block-'));

                // One-time init
                if (!reorganizeInitialized && allBlocks.length > 0) {
                    reorganizeInitialized = true;

                    // Freeze everything
                    allBlocks.forEach(body => {
                        Body.setStatic(body, true);
                        Body.setVelocity(body, { x: 0, y: 0 });
                        Body.setAngularVelocity(body, 0);
                    });

                    // Separate by type
                    const iBlocks = allBlocks.filter(b => b.label === 'block-I');
                    const oBlocks = allBlocks.filter(b => b.label === 'block-O');
                    const otherBlocks = allBlocks.filter(b =>
                        b.label === 'block-T' || b.label === 'block-L' || b.label === 'block-Z'
                    );

                    // Compute tower positions for I and O
                    const towerTargets = computeTowerTargets(iBlocks, oBlocks);
                    towerTargets.forEach((entry, bodyId) => {
                        targetMapRef.current.set(bodyId, entry);
                    });

                    // Mark T, L, Z for removal (animate off-screen upward)
                    otherBlocks.forEach(body => {
                        targetMapRef.current.set(body.id, {
                            target: { x: body.position.x, y: -300 },
                            targetAngle: body.angle,
                            settled: false,
                            remove: true
                        });
                    });
                }

                // Animate all blocks
                const lerp = 0.055;
                allBlocks.forEach(body => {
                    const entry = targetMapRef.current.get(body.id);
                    if (!entry || entry.settled) return;

                    const { target, targetAngle } = entry;
                    const dx = target.x - body.position.x;
                    const dy = target.y - body.position.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);

                    // Lerp position
                    Body.setPosition(body, {
                        x: body.position.x + dx * lerp,
                        y: body.position.y + dy * lerp
                    });

                    // Lerp angle toward target
                    const angleDiff = targetAngle - body.angle;
                    Body.setAngle(body, body.angle + angleDiff * lerp);

                    // Snap or remove when close
                    if (dist < 1.5) {
                        if (entry.remove) {
                            bodiesToRemove.push(body);
                        } else {
                            Body.setPosition(body, target);
                            Body.setAngle(body, targetAngle);
                        }
                        entry.settled = true;
                    }
                });

                // Remove T/L/Z blocks that reached off-screen
                while (bodiesToRemove.length > 0) {
                    const b = bodiesToRemove.pop();
                    Composite.remove(world, b);
                }
            } else {
                // Normal mode: cleanup blocks that fell off-screen
                bodies.forEach(body => {
                    if (body.position.y > H + 100) {
                        Composite.remove(world, body);
                    }
                });
            }
        });

        // Lancement
        Render.run(render);
        const runner = Runner.create();
        runnerRef.current = runner;
        Runner.run(runner, engine);

        // Gestion du redimensionnement fenêtre
        const handleResize = () => {
            const newW = sceneRef.current?.offsetWidth || W;
            const newH = sceneRef.current?.offsetHeight || H;
            render.canvas.width = newW;
            render.canvas.height = newH;
            Body.setPosition(ground, { x: newW / 2, y: newH });
            Body.setPosition(rightWall, { x: newW * 0.96, y: newH / 2 });
            Body.setPosition(leftWall, { x: newW * 0.50, y: newH / 2 });
        };
        window.addEventListener('resize', handleResize);

        // Cleanup function (important pour React)
        return () => {
            isRunning = false; // Arrêt de la boucle dynamique
            window.removeEventListener('resize', handleResize);
            Render.stop(render);
            Runner.stop(runner);
            if (render.canvas) render.canvas.remove();
            Composite.clear(world);
            Engine.clear(engine);
        };
    }, []);

    return (
        <div
            ref={sceneRef}
            style={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: -1 // Pour rester en arrière-plan
            }}
        />
    );
};

export default TrickyTowerBackground;